import { Box, Stack } from '@mui/material';
import { useMemo } from 'react';
import * as React from 'react';

import { appbarHeight } from '@/components/architecture/Layout/AppBar/AppBar';
import { ContextMenuButton } from '@/components/form/baseInputs/Button/ContextMenuButton';
import { Grid } from '@/components/layout/Grid';
import { FOOTER_HEIGHT } from '@/components/navigation/Footer';
import { PageHeader } from '@/components/navigation/PageHeader';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { useReportError } from '@/hooks/useReportError';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { DeleteEntityMenuItem } from '@/modules/entities/components/DeleteEntityMenuItem';
import { NEW_ENTITY_SENTINEL } from '@/modules/entities/entities.constants';
import { getNodes } from '@/utils/graphqlUtils';

import { useAssociatedEntityProposalCountQuery } from './graphql/DesignerLayout.generated';

export type DesignerLayoutProps = React.PropsWithChildren<{
  entityId: string;
  heading: React.ReactNode;
  isFullScreenModal?: boolean;
  LeftPaneContent: React.ReactElement;
  RightPaneContent: React.ReactElement;
  FooterActionContent: React.ReactElement;
  onFormSubmit: () => void;
  setNavigationBlocking: (isNavigationBlocked: boolean) => void;
  onDelete?: () => void;
}>;

const LEFT_PANE_COLUMNS = 4;

function useGetEntityAssociations(entityId: string) {
  const { data, error, loading } = useAssociatedEntityProposalCountQuery({
    variables: {
      entityId,
    },
    fetchPolicy: 'cache-and-network',
    skip: entityId === NEW_ENTITY_SENTINEL,
  });

  useReportError(
    'failed to fetch proposal count for the designer layout',
    error,
    {
      entityId,
    }
  );

  const associatedProposalCount = useMemo(() => {
    if (!data) return 0;
    return data.proposals.totalCount;
  }, [data]);

  const associatedHypotheticalWaterfallCount = useMemo(() => {
    if (!data) return 0;
    return (
      getNodes(data.entities)[0]?.hypotheticalGrats?.flatMap(
        (hg) => hg.hypotheticalWaterfall
      ).length ?? 0
    );
  }, [data]);

  return {
    associatedProposalCount,
    associatedHypotheticalWaterfallCount,
    error,
    loading,
  };
}

function getAssociatedProposalsBadgeDisplayText(
  associatedProposalCount: number
): string {
  if (associatedProposalCount === 1) {
    return `Used in ${associatedProposalCount} proposal`;
  }

  return `Used in ${associatedProposalCount} proposals`;
}

function getAssociatedHypotheticalWaterfallsBadgeDisplayText(
  associatedHypotheticalWaterfallCount: number
): string {
  if (associatedHypotheticalWaterfallCount === 1) {
    return `Used in ${associatedHypotheticalWaterfallCount} hypothetical waterfall`;
  }
  return `Used in ${associatedHypotheticalWaterfallCount} hypothetical waterfalls`;
}

export function DesignerLayout({
  heading,
  LeftPaneContent,
  RightPaneContent,
  FooterActionContent,
  onFormSubmit,
  isFullScreenModal = false,
  setNavigationBlocking,
  entityId,
  onDelete,
}: DesignerLayoutProps) {
  const canDeleteEntity =
    useHasBehaviorAuthorization(
      BehaviorAuthorizationType.CAN_DELETE_ENTITIES_AND_PROPOSALS
    ) && onDelete;
  const { associatedProposalCount, associatedHypotheticalWaterfallCount } =
    useGetEntityAssociations(entityId);

  const height = useMemo(() => {
    if (isFullScreenModal) {
      return `calc(100vh - ${FOOTER_HEIGHT}px)`;
    }

    return `calc(100vh - ${FOOTER_HEIGHT}px - ${appbarHeight})`;
  }, [isFullScreenModal]);

  return (
    <Stack component="form" noValidate onSubmit={onFormSubmit}>
      <Stack
        sx={{
          height,
          overflow: 'auto',
        }}
      >
        <PageHeader
          heading={heading}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              {associatedProposalCount !== 0 ? (
                <Badge
                  variant={BadgeVariants.Warning}
                  display={getAssociatedProposalsBadgeDisplayText(
                    associatedProposalCount
                  )}
                />
              ) : undefined}
              {associatedHypotheticalWaterfallCount !== 0 ? (
                <Badge
                  variant={BadgeVariants.Warning}
                  display={getAssociatedHypotheticalWaterfallsBadgeDisplayText(
                    associatedHypotheticalWaterfallCount
                  )}
                />
              ) : undefined}
              {entityId !== NEW_ENTITY_SENTINEL && canDeleteEntity && (
                <ContextMenuButton>
                  <DeleteEntityMenuItem
                    label="Delete draft"
                    modalProps={{
                      heading: 'Are you sure you want to delete this draft?',
                      children: `Deleting will also remove this draft from any associated proposals and delete any client proposals with no remaining drafts.`,
                    }}
                    entityId={entityId}
                    onDelete={() => {
                      setNavigationBlocking(false);
                      onDelete?.();
                    }}
                  />
                </ContextMenuButton>
              )}
            </Stack>
          }
        />

        <Stack data-it="DesignerLayout-childrenContainer" flex={1}>
          <Stack flex={1}>
            <Stack p={2} flex={1}>
              <Grid container spacing={2} flex={1} columns={12}>
                <Grid height="100%" item sm={LEFT_PANE_COLUMNS}>
                  {LeftPaneContent}
                </Grid>
                <Grid item sm={12 - LEFT_PANE_COLUMNS}>
                  {RightPaneContent}
                </Grid>
              </Grid>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Box>{FooterActionContent}</Box>
    </Stack>
  );
}
